import React, { CSSProperties, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { MandatoryChildren } from '../../../models/children-props';
import { isEqual } from 'lodash';

export type OutsideClickWrapperViewProps = MandatoryChildren<{
  //
  component?: string;
  className?: string;
  style?: CSSProperties;
  onClickOutside?: (event: MouseEvent | TouchEvent) => void;
  onFocusOutside?: (event: FocusEvent) => void;
  id?: string;
}>;

export const OutsideClickWrapperView = forwardRef<HTMLElement, OutsideClickWrapperViewProps>(
  ({ component, onClickOutside, onFocusOutside, children, style, className, id }, ref): JSX.Element => {
    const [_, setClickCaptured] = useState(false);
    const [__, setFocusCaptured] = useState(false);
    const documentSizesRef = useRef<ResizeObserverSize[][] | null>(null);
    // we use document sizes ref to store document box sizes for the observer

    const documentResize: ResizeObserverCallback = useCallback(
      entries => {
        const sizes = entries.map(value => {
          // we need sizes, since Safari does not support devicePixelContentBoxSize, and it's not marked as optional
          // prop in types. This is to prevent iterable null or undefined
          const sizesArray = value.devicePixelContentBoxSize || value.contentBoxSize;
          return [...sizesArray];
        });
        // we get all the sizes of enteries
        let clickOutside = false;
        // we don't want to trigger outside click before we check if we need to
        if (documentSizesRef.current) {
          if (isEqual(documentSizesRef.current, sizes)) {
            return;
            // if the resize is triggered but the sizes are the same (e.g. rerender) we don't want to do a thing
          } else {
            // it means we are goind to perform outside click, and we need to set new documentRefSizes
            clickOutside = true;
            documentSizesRef.current = sizes;
          }
        } else {
          // we don't have any sizes, since the component just have been rendered. we just set new sizes
          documentSizesRef.current = sizes;
        }
        if (!clickOutside) return;
        setClickCaptured(oldClickCapured => {
          if (!oldClickCapured && onClickOutside) {
            const evt = new MouseEvent('mouseup', {
              view: window,
              bubbles: true,
              cancelable: true,
              clientX: 0,
              clientY: 0
            });
            document.dispatchEvent(evt);
          }
          return false;
        });
      },
      [onClickOutside]
    );

    const [resizeObserver] = useState<ResizeObserver>(new ResizeObserver(documentResize));

    useEffect(() => {
      resizeObserver.observe(document.body);
      return () => resizeObserver.disconnect();
    }, [resizeObserver]);

    const innerClick = () => {
      setClickCaptured(true);
    };

    const documentClick = useCallback(
      (event: MouseEvent | TouchEvent) => {
        setClickCaptured(oldClickCapured => {
          if (!oldClickCapured && onClickOutside) {
            onClickOutside(event);
          }
          return false;
        });
      },
      [onClickOutside]
    );

    const innerFocus = () => {
      setFocusCaptured(true);
    };

    const documentFocus = useCallback(
      (event: FocusEvent) => {
        setFocusCaptured(oldFocusCaptured => {
          if (!oldFocusCaptured && onFocusOutside) {
            onFocusOutside(event);
          }
          return false;
        });
      },
      [onFocusOutside]
    );

    useEffect(() => {
      document.addEventListener('mouseup', documentClick);
      document.addEventListener('touchstart', documentClick);
      document.addEventListener('focusin', documentFocus);

      return () => {
        // we use setTimeout with 0 value here to make sure rendering is done before we unmount it. because it can
        // happened that parent component has been unmounted first and then the nested child would stay hanging.
        // this fix prevents it
        setTimeout(() => {
          document.removeEventListener('mouseup', documentClick);
          document.removeEventListener('touchstart', documentClick);
          document.removeEventListener('focusin', documentFocus);
        }, 0);
      };
    }, [documentClick, documentFocus]);

    const getProps = () => ({
      onMouseDown: innerClick,
      onTouchStart: innerClick,
      onFocus: innerFocus,
      ...(style ? { style } : {}),
      ...(className ? { className } : {}),
      ...(ref ? { ref } : {}),
      ...(id ? { id } : {})
    });

    return React.createElement(component ?? 'div', getProps(), children);
  }
);

OutsideClickWrapperView.displayName = 'OutsideClickWrapperView';
